import { useState, useEffect, useRef, useCallback } from "react";
import storage from "./storage";

const QUESTIONS = [
  {
    id: "name",
    layer: 0,
    label: "Core Details",
    question: "What's your first name?",
    type: "text",
    placeholder: "e.g. James",
    required: true,
  },
  {
    id: "tax_year",
    layer: 0,
    label: "Core Details",
    question: "Which tax year are we optimising for?",
    type: "select",
    options: ["2025/26", "2026/27"],
    required: true,
  },
  {
    id: "age",
    layer: 0,
    label: "Core Details",
    question: "How old are you?",
    type: "number",
    placeholder: "e.g. 35",
    required: true,
  },
  {
    id: "region",
    layer: 0,
    label: "Core Details",
    question: "Where do you live in the UK?",
    type: "select",
    options: ["England", "Scotland", "Wales", "Northern Ireland"],
    required: true,
  },
  {
    id: "residency",
    layer: 0,
    label: "Core Details",
    question: "Are you a UK tax resident for the full tax year?",
    type: "select",
    options: ["Yes", "No — split year", "Non-domiciled"],
    required: true,
  },
  {
    id: "gross_salary",
    layer: 1,
    label: "Income",
    question: "What is your annual gross salary (before tax)?",
    type: "currency",
    placeholder: "e.g. 65000",
    required: true,
  },
  {
    id: "employer_count",
    layer: 1,
    label: "Income",
    question: "How many employers do you have?",
    type: "select",
    options: ["1", "2", "3 or more"],
    required: true,
  },
  {
    id: "bonus_income",
    layer: 1,
    label: "Income",
    question: "Do you receive any annual bonus or commission?",
    type: "currency_optional",
    placeholder: "Enter amount or leave blank if none",
    required: false,
  },
  {
    id: "other_income",
    layer: 1,
    label: "Income",
    question: "Do you have any other income sources? (Select all that apply)",
    type: "multiselect",
    options: [
      "Rental income",
      "Dividend income",
      "Savings interest",
      "Freelance / self-employed income",
      "Foreign income",
      "None of the above",
    ],
    required: true,
  },
  {
    id: "rental_income",
    layer: 1,
    label: "Income",
    question: "What is your annual rental income (gross)?",
    type: "currency",
    placeholder: "e.g. 12000",
    required: true,
    condition: (answers) =>
      answers.other_income && answers.other_income.includes("Rental income"),
  },
  {
    id: "dividend_income",
    layer: 1,
    label: "Income",
    question: "What is your annual dividend income?",
    type: "currency",
    placeholder: "e.g. 5000",
    required: true,
    condition: (answers) =>
      answers.other_income && answers.other_income.includes("Dividend income"),
  },
  {
    id: "freelance_income",
    layer: 1,
    label: "Income",
    question: "What is your annual freelance / self-employed income?",
    type: "currency",
    placeholder: "e.g. 8000",
    required: true,
    condition: (answers) =>
      answers.other_income &&
      answers.other_income.includes("Freelance / self-employed income"),
  },
  {
    id: "marital_status",
    layer: 2,
    label: "Family",
    question: "What is your marital / civil partnership status?",
    type: "select",
    options: ["Single", "Married / Civil Partner", "Separated / Divorced", "Widowed"],
    required: true,
  },
  {
    id: "partner_income",
    layer: 2,
    label: "Family",
    question: "What is your spouse/partner's approximate annual income?",
    type: "select",
    options: [
      "No income / under £12,570",
      "£12,570 – £50,270",
      "£50,270 – £100,000",
      "Over £100,000",
    ],
    required: true,
    condition: (answers) => answers.marital_status === "Married / Civil Partner",
  },
  {
    id: "children",
    layer: 2,
    label: "Family",
    question: "Do you have children under 18?",
    type: "select",
    options: ["No", "1", "2", "3", "4 or more"],
    required: true,
  },
  {
    id: "child_benefit",
    layer: 2,
    label: "Family",
    question: "Do you currently claim Child Benefit?",
    type: "select",
    options: ["Yes", "No", "Opted out due to high income"],
    required: true,
    condition: (answers) => answers.children && answers.children !== "No",
  },
  {
    id: "childcare",
    layer: 2,
    label: "Family",
    question: "Do you use any childcare arrangements?",
    type: "multiselect",
    options: [
      "Tax-Free Childcare account",
      "Employer childcare vouchers",
      "Workplace nursery",
      "None",
    ],
    required: true,
    condition: (answers) => answers.children && answers.children !== "No",
  },
  {
    id: "pension_type",
    layer: 3,
    label: "Pensions & Savings",
    question: "What type of workplace pension do you have?",
    type: "select",
    options: [
      "Salary sacrifice",
      "Net pay arrangement",
      "Relief at source",
      "I'm not sure",
      "No workplace pension",
    ],
    required: true,
  },
  {
    id: "pension_contribution_pct",
    layer: 3,
    label: "Pensions & Savings",
    question: "What percentage of salary do you contribute to your pension?",
    type: "number",
    placeholder: "e.g. 5",
    suffix: "%",
    required: true,
    condition: (answers) => answers.pension_type !== "No workplace pension",
  },
  {
    id: "employer_pension_pct",
    layer: 3,
    label: "Pensions & Savings",
    question: "What percentage does your employer contribute?",
    type: "number",
    placeholder: "e.g. 3",
    suffix: "%",
    required: true,
    condition: (answers) => answers.pension_type !== "No workplace pension",
  },
  {
    id: "pension_carry_forward",
    layer: 3,
    label: "Pensions & Savings",
    question:
      "Have you used your full £60,000 annual pension allowance in each of the last 3 tax years?",
    type: "select",
    options: ["Yes — all used", "No — I have unused allowance", "I'm not sure"],
    required: true,
  },
  {
    id: "isa_usage",
    layer: 3,
    label: "Pensions & Savings",
    question: "Which ISAs have you contributed to this tax year?",
    type: "multiselect",
    options: [
      "Cash ISA",
      "Stocks & Shares ISA",
      "Lifetime ISA",
      "Innovative Finance ISA",
      "None",
    ],
    required: true,
  },
  {
    id: "isa_amount",
    layer: 3,
    label: "Pensions & Savings",
    question: "Approximately how much have you put into ISAs this year (total)?",
    type: "currency",
    placeholder: "e.g. 10000",
    required: true,
    condition: (answers) =>
      answers.isa_usage &&
      !answers.isa_usage.includes("None") &&
      answers.isa_usage.length > 0,
  },
  {
    id: "employer_share_schemes",
    layer: 4,
    label: "Employment Benefits",
    question: "Does your employer offer any share schemes? (Select all that apply)",
    type: "multiselect",
    options: ["SIP (Share Incentive Plan)", "SAYE (Sharesave)", "EMI options", "CSOP", "None"],
    required: true,
  },
  {
    id: "sip_contribution",
    layer: 4,
    label: "Employment Benefits",
    question: "How much do you contribute to SIP per month?",
    type: "currency",
    placeholder: "e.g. 150",
    required: true,
    condition: (answers) =>
      answers.employer_share_schemes &&
      answers.employer_share_schemes.includes("SIP (Share Incentive Plan)"),
  },
  {
    id: "company_car",
    layer: 4,
    label: "Employment Benefits",
    question: "Do you have a company car or car allowance?",
    type: "select",
    options: ["Company car (petrol/diesel)", "Company car (electric/hybrid)", "Car allowance (cash)", "Neither"],
    required: true,
  },
  {
    id: "car_allowance_amount",
    layer: 4,
    label: "Employment Benefits",
    question: "What is your annual car allowance (before tax)?",
    type: "currency",
    placeholder: "e.g. 6000",
    required: true,
    condition: (answers) => answers.company_car === "Car allowance (cash)",
  },
  {
    id: "company_car_list_price",
    layer: 4,
    label: "Employment Benefits",
    question: "What is the list price (P11D value) of your company car?",
    type: "currency",
    placeholder: "e.g. 35000",
    required: true,
    condition: (answers) => answers.company_car === "Company car (petrol/diesel)" || answers.company_car === "Company car (electric/hybrid)",
  },
  {
    id: "wfh",
    layer: 4,
    label: "Employment Benefits",
    question: "Do you work from home regularly?",
    type: "select",
    options: ["Yes — employer requires it", "Yes — by choice / hybrid", "No — fully office-based"],
    required: true,
  },
  {
    id: "professional_subs",
    layer: 4,
    label: "Employment Benefits",
    question:
      "Do you pay for any professional body memberships? (e.g. ACCA, CISI, BCS, RICS, Law Society)",
    type: "select",
    options: ["Yes", "No"],
    required: true,
  },
  {
    id: "cycle_to_work",
    layer: 4,
    label: "Employment Benefits",
    question: "Are you using or interested in the Cycle to Work scheme?",
    type: "select",
    options: ["Already using it", "Interested", "Not interested"],
    required: true,
  },
  {
    id: "tax_efficient_investments",
    layer: 5,
    label: "Investments & Reliefs",
    question: "Do you hold or plan to hold any tax-efficient investments?",
    type: "multiselect",
    options: ["EIS", "SEIS", "VCT", "Social Investment Tax Relief", "None"],
    required: true,
  },
  {
    id: "property_owned",
    layer: 5,
    label: "Investments & Reliefs",
    question: "Do you own any property?",
    type: "multiselect",
    options: ["Main residence (mortgage)", "Main residence (owned outright)", "Buy-to-let property", "None / renting"],
    required: true,
  },
  {
    id: "rent_a_room",
    layer: 5,
    label: "Investments & Reliefs",
    question: "Do you rent out a room in your main home?",
    type: "select",
    options: ["Yes", "No", "Considering it"],
    required: true,
    condition: (answers) =>
      answers.property_owned &&
      (answers.property_owned.includes("Main residence (mortgage)") ||
        answers.property_owned.includes("Main residence (owned outright)")),
  },
  {
    id: "gift_aid",
    layer: 5,
    label: "Investments & Reliefs",
    question: "Do you make charitable donations via Gift Aid?",
    type: "select",
    options: ["Yes — regularly", "Occasionally", "No"],
    required: true,
  },
  {
    id: "gift_aid_amount",
    layer: 5,
    label: "Investments & Reliefs",
    question: "Approximately how much do you donate to charity per year (Gift Aid)?",
    type: "currency",
    placeholder: "e.g. 500",
    required: true,
    condition: (answers) =>
      answers.gift_aid === "Yes — regularly" || answers.gift_aid === "Occasionally",
  },
  {
    id: "student_loan",
    layer: 6,
    label: "Other",
    question: "Do you have a student loan?",
    type: "select",
    options: ["Plan 1", "Plan 2", "Plan 4 (Scotland)", "Plan 5", "Postgraduate loan", "No student loan"],
    required: true,
  },
  {
    id: "goals",
    layer: 6,
    label: "Other",
    question: "What's most important to you right now?",
    type: "multiselect",
    options: [
      "Maximise take-home pay now",
      "Build long-term wealth (pensions/investments)",
      "Save for a first home",
      "Reduce tax bill specifically",
      "Plan for retirement",
      "Protect family financially",
    ],
    required: true,
  },
  {
    id: "risk_appetite",
    layer: 6,
    label: "Other",
    question: "How would you describe your appetite for tax-efficient but higher-risk investments (e.g. EIS/SEIS)?",
    type: "select",
    options: ["Conservative — low risk only", "Moderate — some risk acceptable", "Aggressive — happy with venture-level risk"],
    required: true,
  },
];

const LAYER_LABELS = [
  "Core Details",
  "Income",
  "Family",
  "Pensions & Savings",
  "Employment Benefits",
  "Investments & Reliefs",
  "Goals & Preferences",
];

const LAYER_ICONS = ["👤", "💷", "👨‍👩‍👧‍👦", "🏦", "🏢", "📈", "🎯"];

function generateReport(answers) {
  const salary = parseFloat(answers.gross_salary) || 0;
  const bonus = parseFloat(answers.bonus_income) || 0;
  const totalEmployment = salary + bonus;
  const rentalIncome = parseFloat(answers.rental_income) || 0;
  const dividendIncome = parseFloat(answers.dividend_income) || 0;
  const freelanceIncome = parseFloat(answers.freelance_income) || 0;
  const totalIncome = totalEmployment + rentalIncome + dividendIncome + freelanceIncome;
  const pensionPct = parseFloat(answers.pension_contribution_pct) || 0;
  const employerPensionPct = parseFloat(answers.employer_pension_pct) || 0;
  const currentPensionContrib = salary * (pensionPct / 100);
  const giftAidAmount = parseFloat(answers.gift_aid_amount) || 0;
  const sipMonthly = parseFloat(answers.sip_contribution) || 0;
  const isaAmount = parseFloat(answers.isa_amount) || 0;
  const isScottish = answers.region === "Scotland";
  const isMarried = answers.marital_status === "Married / Civil Partner";
  const partnerLowIncome = answers.partner_income === "No income / under £12,570";
  const hasChildren = answers.children && answers.children !== "No";
  const numChildren = answers.children === "4 or more" ? 4 : parseInt(answers.children) || 0;
  const age = parseInt(answers.age) || 30;

  const recommendations = [];
  let totalPotentialSaving = 0;

  // ========== THE £100K TRAP ==========
  if (totalIncome > 100000 && totalIncome <= 130000) {
    const excessOver100k = totalIncome - 100000;
    const personalAllowanceLost = Math.min(excessOver100k / 2, 12570);
    const additionalTax = personalAllowanceLost * 0.4;
    const pensionNeeded = excessOver100k;
    recommendations.push({
      title: "⚠️ The £100K Trap — Restore Your Personal Allowance",
      priority: "critical",
      saving: additionalTax,
      detail: `Your income of £${totalIncome.toLocaleString()} puts you in the personal allowance taper zone (£100,125–£125,140). You're losing £1 of allowance for every £2 over £100,125 — creating an effective 60% marginal tax rate. By increasing pension contributions by approximately £${Math.ceil(pensionNeeded / 100) * 100} (via salary sacrifice), you could restore your full personal allowance and save approximately £${Math.round(additionalTax).toLocaleString()} in tax. This is typically the single highest-value move for earners in this band.`,
    });
    totalPotentialSaving += additionalTax;
  }

  // ========== PENSION OPTIMISATION ==========
  if (answers.pension_type !== "Salary sacrifice" && salary > 30000) {
    const niSaving = currentPensionContrib * 0.12;
    recommendations.push({
      title: "Switch to Salary Sacrifice Pension",
      priority: "high",
      saving: niSaving,
      detail: `You're not using salary sacrifice for pension contributions. By switching, both you and your employer save National Insurance (employee: 8%, employer: 13.8%). On your current contribution of £${Math.round(currentPensionContrib).toLocaleString()}/year, you could save approximately £${Math.round(niSaving).toLocaleString()} in employee NI alone — and your employer may pass their NI saving into your pension pot too. Ask your HR/payroll team about switching.`,
    });
    totalPotentialSaving += niSaving;
  }

  const maxAnnualAllowance = 60000;
  const totalPensionContrib = salary * ((pensionPct + employerPensionPct) / 100);
  const pensionHeadroom = maxAnnualAllowance - totalPensionContrib;
  if (pensionHeadroom > 5000 && salary > 50270) {
    const taxRate = salary > 125140 ? 0.45 : 0.4;
    const additionalContribSaving = Math.min(pensionHeadroom, 20000) * taxRate;
    recommendations.push({
      title: "Maximise Pension Contributions",
      priority: "high",
      saving: additionalContribSaving,
      detail: `You have approximately £${Math.round(pensionHeadroom).toLocaleString()} of unused pension annual allowance. As a ${taxRate === 0.45 ? "additional" : "higher"}-rate taxpayer, every £1,000 extra you contribute effectively costs you only £${(1000 * (1 - taxRate)).toFixed(0)}. Contributing an additional £${Math.min(pensionHeadroom, 20000).toLocaleString()} could save £${Math.round(additionalContribSaving).toLocaleString()} in income tax. ${answers.pension_carry_forward !== "Yes — all used" ? "You may also have unused allowance from previous years (up to 3 years carry-forward) — check with your pension provider." : ""}`,
    });
    totalPotentialSaving += additionalContribSaving;
  }

  // ========== MARRIAGE ALLOWANCE ==========
  if (isMarried && partnerLowIncome && totalIncome <= 50270) {
    const marriageSaving = 252;
    recommendations.push({
      title: "Claim Marriage Allowance",
      priority: "high",
      saving: marriageSaving,
      detail: `Your spouse/partner earns under £12,570 and you're a basic-rate taxpayer. They can transfer £1,260 of their unused personal allowance to you, saving you £252 per year. You can backdate this for up to 4 years if you haven't claimed before — potentially £1,008. Apply at gov.uk/marriage-allowance.`,
    });
    totalPotentialSaving += marriageSaving;
  }

  // ========== CHILD BENEFIT HIGH INCOME CHARGE ==========
  if (hasChildren && totalIncome > 60000 && totalIncome <= 80000 && answers.child_benefit !== "No") {
    const clawbackRate = Math.min((totalIncome - 60000) / 20000, 1);
    const weeklyBenefit = numChildren === 1 ? 26.05 : 26.05 + (numChildren - 1) * 17.25;
    const annualBenefit = weeklyBenefit * 52;
    const clawback = annualBenefit * clawbackRate;
    const pensionToAvoid = totalIncome - 60000;
    recommendations.push({
      title: "Avoid High Income Child Benefit Charge",
      priority: "high",
      saving: Math.round(clawback),
      detail: `With income of £${totalIncome.toLocaleString()}, you're subject to the High Income Child Benefit Charge (HICBC). You're effectively losing ${Math.round(clawbackRate * 100)}% of your Child Benefit (£${Math.round(clawback)}/year) through the charge. By making pension contributions of £${pensionToAvoid.toLocaleString()} via salary sacrifice, you could bring your adjusted net income below £60,000 and keep the full benefit. This would also save higher-rate tax on that amount.`,
    });
    totalPotentialSaving += Math.round(clawback);
  }

  // ========== SIP ==========
  if (
    answers.employer_share_schemes &&
    answers.employer_share_schemes.includes("SIP (Share Incentive Plan)")
  ) {
    if (sipMonthly < 200) {
      const additionalMonthly = 200 - sipMonthly;
      const annualSaving = additionalMonthly * 12 * 0.32;
      recommendations.push({
        title: "Maximise SIP Contributions",
        priority: "medium",
        saving: Math.round(annualSaving),
        detail: `You're contributing £${sipMonthly}/month to SIP but the maximum is £200/month. SIP contributions are taken before tax and NI, saving you 20% income tax + 12% NI = 32% (or 42% if higher-rate). Increasing by £${additionalMonthly}/month could save approximately £${Math.round(annualSaving)}/year in tax and NI. Hold the shares for 5 years and there's no income tax or NI to pay at all.`,
      });
      totalPotentialSaving += Math.round(annualSaving);
    }
  } else if (
    !answers.employer_share_schemes ||
    answers.employer_share_schemes.includes("None")
  ) {
    recommendations.push({
      title: "Check If Your Employer Offers Share Schemes",
      priority: "info",
      saving: 0,
      detail: `Share Incentive Plans (SIP), Sharesave (SAYE), and other HMRC-approved share schemes offer significant tax advantages. SIP alone could save up to £768/year in tax and NI at basic rate (£200/month × 32%). Ask your employer if they offer any share schemes — many large employers do but employees aren't always aware.`,
    });
  }

  // ========== ISA OPTIMISATION ==========
  const isaAllowance = 20000;
  if (isaAmount < isaAllowance && salary > 30000) {
    const remainingIsa = isaAllowance - isaAmount;
    recommendations.push({
      title: "Use Remaining ISA Allowance",
      priority: "medium",
      saving: 0,
      detail: `You've used £${isaAmount.toLocaleString()} of your £20,000 ISA allowance. You have £${remainingIsa.toLocaleString()} remaining. While ISA contributions don't reduce your tax bill directly, all growth, dividends, and interest within ISAs are completely tax-free. ${age < 40 && answers.goals && answers.goals.includes("Save for a first home") ? "Consider a Lifetime ISA — you'll get a 25% government bonus (up to £1,000/year) on contributions towards your first home or retirement." : "A Stocks & Shares ISA is particularly powerful for higher-rate taxpayers who would otherwise pay 33.75% on dividends above the £1,000 allowance."}`,
    });
  }

  // ========== EIS / SEIS / VCT ==========
  if (
    answers.risk_appetite !== "Conservative — low risk only" &&
    salary > 50270
  ) {
    if (
      !answers.tax_efficient_investments ||
      answers.tax_efficient_investments.includes("None")
    ) {
      const eisRelief = 10000 * 0.3;
      const seisRelief = 5000 * 0.5;
      recommendations.push({
        title: "Consider EIS / SEIS Investments",
        priority: "medium",
        saving: eisRelief + seisRelief,
        detail: `As a higher-rate taxpayer with moderate+ risk appetite, EIS and SEIS offer powerful tax relief. EIS gives 30% income tax relief (invest £10,000, get £3,000 off your tax bill) plus CGT deferral and no CGT on gains if held 3+ years. SEIS gives 50% relief on investments up to £200,000/year. A modest portfolio of £10,000 EIS + £5,000 SEIS could save £5,500 in income tax. These are higher-risk investments in small companies — only invest what you can afford to lose.`,
      });
      totalPotentialSaving += eisRelief + seisRelief;
    }
  }

  // ========== GIFT AID ==========
  if (giftAidAmount > 0 && salary > 50270) {
    const higherRateRelief = giftAidAmount * 0.25 * 0.2;
    recommendations.push({
      title: "Claim Higher-Rate Gift Aid Relief",
      priority: "medium",
      saving: Math.round(higherRateRelief),
      detail: `You donate £${giftAidAmount.toLocaleString()}/year via Gift Aid. The charity claims 25% basic rate tax, but as a higher-rate taxpayer, you can claim the difference (20%) through self-assessment. That's £${Math.round(higherRateRelief)} back. Make sure you complete a Self Assessment tax return to claim this — it won't happen automatically. You can also carry back Gift Aid donations to the previous tax year if it's more beneficial.`,
    });
    totalPotentialSaving += Math.round(higherRateRelief);
  }

  // ========== WORKING FROM HOME ==========
  if (answers.wfh === "Yes — employer requires it") {
    recommendations.push({
      title: "Claim Working From Home Allowance",
      priority: "low",
      saving: 62,
      detail: `As your employer requires you to work from home, you can claim tax relief on £6/week (£312/year) without receipts. At basic rate that's £62.40/year, or £124.80 at higher rate. Apply via gov.uk/tax-relief-for-employees/working-at-home. You may also claim more if your actual additional costs are higher (heating, electricity, broadband) — but you'll need records.`,
    });
    totalPotentialSaving += salary > 50270 ? 125 : 62;
  }

  // ========== PROFESSIONAL SUBSCRIPTIONS ==========
  if (answers.professional_subs === "Yes") {
    recommendations.push({
      title: "Claim Professional Subscription Relief",
      priority: "low",
      saving: 100,
      detail: `If your professional body is on HMRC's approved list (gov.uk/government/publications/professional-bodies-approved-for-tax-relief-list-3), you can claim tax relief on the full subscription fee. This is claimed via Self Assessment or by contacting HMRC directly. Typical savings are £50–£200/year depending on the subscription cost and your tax rate.`,
    });
    totalPotentialSaving += 100;
  }

  // ========== RENT A ROOM ==========
  if (answers.rent_a_room === "Considering it" || answers.rent_a_room === "Yes") {
    if (answers.rent_a_room === "Considering it") {
      recommendations.push({
        title: "Rent-a-Room Scheme — Tax-Free Income",
        priority: "medium",
        saving: 0,
        detail: `The Rent-a-Room scheme lets you earn up to £7,500/year tax-free by renting out a furnished room in your main home. This applies to lodgers or even Airbnb guests using a spare room. No need to declare it if you stay under the threshold. At higher-rate tax, £7,500 of rental income would normally cost you £3,000 in tax — this scheme eliminates that entirely.`,
      });
    }
  }

  // ========== COMPANY CAR ==========
  if (answers.company_car === "Company car (petrol/diesel)") {
    const carPrice = parseFloat(answers.company_car_list_price) || 40000;
    const currentBiKRate = 0.30; // average petrol/diesel
    const evBiKRate = 0.02;
    const taxRate = salary > 50270 ? 0.40 : 0.20;
    const currentBiKTax = carPrice * currentBiKRate * taxRate;
    const evBiKTax = carPrice * evBiKRate * taxRate;
    const saving = Math.round(currentBiKTax - evBiKTax);
    recommendations.push({
      title: "Consider Switching to Electric Company Car",
      priority: "medium",
      saving: saving,
      detail: `Your company car (P11D value ~£${carPrice.toLocaleString()}) attracts an estimated BiK rate of ~30%, costing you ~£${Math.round(currentBiKTax).toLocaleString()}/year in BiK tax at ${taxRate === 0.4 ? "higher" : "basic"} rate. An equivalent electric vehicle at 2% BiK would cost just ~£${Math.round(evBiKTax).toLocaleString()}/year — a saving of ~£${saving.toLocaleString()}. If your employer offers electric car schemes (e.g. Octopus Electric Vehicles, Tusker), the savings can be substantial. Factor in free workplace charging and lower running costs too.`,
    });
    totalPotentialSaving += saving;
  }

  if (answers.company_car === "Car allowance (cash)") {
    const carAllowance = parseFloat(answers.car_allowance_amount) || 6000;
    const taxRate = salary > 50270 ? 0.40 : 0.20;
    const niRate = salary > 50270 ? 0.02 : 0.08;
    const taxOnAllowance = carAllowance * (taxRate + niRate);
    // EV salary sacrifice: give up the allowance, get an EV with 2% BiK
    const evListPrice = 35000; // reasonable mid-range EV
    const evBiKTax = evListPrice * 0.02 * taxRate;
    const saving = Math.round(taxOnAllowance - evBiKTax);
    recommendations.push({
      title: "Consider Salary Sacrifice EV Instead of Cash Allowance",
      priority: "medium",
      saving: saving,
      detail: `Your £${carAllowance.toLocaleString()}/year cash car allowance is taxed as income — you're paying ~£${Math.round(taxOnAllowance).toLocaleString()}/year in income tax and NI on it. A salary sacrifice electric vehicle scheme would replace this with a BiK charge of just 2%. On a ~£35,000 EV, that's only ~£${Math.round(evBiKTax).toLocaleString()}/year in BiK tax, saving you approximately £${saving.toLocaleString()}/year. Many employers now offer this — the net monthly cost is often less than the tax hit on your cash allowance. Worth modelling the exact numbers with your employer.`,
    });
    totalPotentialSaving += saving;
  }

  // ========== LIFETIME ISA for first-time buyers ==========
  if (
    age < 40 &&
    answers.goals &&
    answers.goals.includes("Save for a first home") &&
    answers.isa_usage &&
    !answers.isa_usage.includes("Lifetime ISA")
  ) {
    recommendations.push({
      title: "Open a Lifetime ISA for First Home",
      priority: "high",
      saving: 1000,
      detail: `You're under 40 and saving for a first home — a Lifetime ISA gives you a 25% government bonus on contributions up to £4,000/year (that's a free £1,000). You can use it to buy a first home up to £450,000. This is essentially a guaranteed 25% return on your savings. Open one before your 40th birthday even if you can only contribute a small amount initially.`,
    });
    totalPotentialSaving += 1000;
  }

  // ========== SCOTTISH TAX ==========
  if (isScottish && salary > 43662) {
    recommendations.push({
      title: "Scottish Tax Bands — Additional Planning Needed",
      priority: "info",
      saving: 0,
      detail: `As a Scottish taxpayer, your income tax rates and bands differ from the rest of the UK. Scotland has six bands (starter 19%, basic 20%, intermediate 21%, higher 42%, advanced 45%, top 48%). This means pension contributions and other reliefs can have different marginal values. All recommendations above account for this, but it's worth reviewing with a Scottish-specialist adviser to optimise across the unique band structure.`,
    });
  }

  // ========== TRADING & PROPERTY ALLOWANCE ==========
  if (freelanceIncome > 0 && freelanceIncome <= 1000) {
    recommendations.push({
      title: "Use the £1,000 Trading Allowance",
      priority: "low",
      saving: Math.round(freelanceIncome * 0.2),
      detail: `Your freelance income of £${freelanceIncome.toLocaleString()} falls within the £1,000 trading allowance. This means you don't need to declare it or pay any tax on it. No Self Assessment needed for this income alone.`,
    });
    totalPotentialSaving += Math.round(freelanceIncome * 0.2);
  }

  // Sort by priority
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  recommendations.sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  );

  return { recommendations, totalPotentialSaving, totalIncome };
}

// ===== STYLES =====
const palette = {
  bg: "#0C1117",
  surface: "#151C25",
  surfaceHover: "#1A2332",
  border: "#1E2A3A",
  borderFocus: "#2E6B4F",
  text: "#E2E8F0",
  textMuted: "#7B8BA3",
  textDim: "#4A5568",
  accent: "#34D399",
  accentDark: "#059669",
  accentBg: "rgba(52, 211, 153, 0.08)",
  critical: "#F87171",
  criticalBg: "rgba(248, 113, 113, 0.08)",
  high: "#FBBF24",
  highBg: "rgba(251, 191, 36, 0.08)",
  medium: "#60A5FA",
  mediumBg: "rgba(96, 165, 250, 0.08)",
  low: "#A78BFA",
  lowBg: "rgba(167, 139, 250, 0.08)",
  info: "#7B8BA3",
  infoBg: "rgba(123, 139, 163, 0.06)",
};

const priorityColors = {
  critical: { color: palette.critical, bg: palette.criticalBg, label: "CRITICAL" },
  high: { color: palette.high, bg: palette.highBg, label: "HIGH IMPACT" },
  medium: { color: palette.medium, bg: palette.mediumBg, label: "MEDIUM" },
  low: { color: palette.low, bg: palette.lowBg, label: "LOW" },
  info: { color: palette.info, bg: palette.infoBg, label: "INFO" },
};

function PensionSlider({ pensionCalc, baseline, answers, isScottish, calcFullTax, palette }) {
  const [additionalContrib, setAdditionalContrib] = useState(0);
  const defaultMech = answers.pension_type === "Salary sacrifice" ? "sal_sac" : answers.pension_type === "Net pay arrangement" ? "net_pay" : "relief_at_source";
  const [mechanism, setMechanism] = useState(defaultMech);
  const pc = pensionCalc;
  const isSalSac = mechanism === "sal_sac";
  const isNetPay = mechanism === "net_pay";
  const usesReliefAtSource = mechanism === "relief_at_source";

  // For salary sacrifice / net pay: the additional contrib reduces taxable pay directly
  // For relief at source: you pay 80% of gross, HMRC adds 20%, and higher-rate relief is claimed via SA
  
  let taxCalcContrib; // the amount that reduces taxable income for tax calc purposes
  let outOfPocket; // what actually leaves your bank/payslip
  let hmrcTopUp = 0; // government adds this to your pot (relief at source only)
  let higherRateReclaim = 0; // you claim this back via Self Assessment
  let totalIntoPot; // total that ends up in pension
  let employerNISaved = 0;

  if (isSalSac) {
    // Salary sacrifice: gross amount is sacrificed from pay before tax AND NI
    taxCalcContrib = additionalContrib;
    outOfPocket = additionalContrib; // reduces gross pay by this much
    totalIntoPot = additionalContrib;
    employerNISaved = additionalContrib * 0.138;
    // Out of pocket in real terms = reduction in take-home, calculated below
  } else if (isNetPay) {
    // Net pay: deducted from gross before tax, but NI still applies on full salary
    taxCalcContrib = additionalContrib;
    outOfPocket = additionalContrib;
    totalIntoPot = additionalContrib;
  } else {
    // Relief at source: you pay net (80%), HMRC adds basic rate (20%)
    // The gross contribution is the slider amount
    hmrcTopUp = additionalContrib * 0.20; // HMRC adds 20% of gross
    outOfPocket = additionalContrib * 0.80; // you only pay 80%
    taxCalcContrib = additionalContrib; // still reduces adjusted net income by gross amount
    totalIntoPot = additionalContrib; // outOfPocket + hmrcTopUp = gross
    
    // Higher/additional rate taxpayers can reclaim the difference via SA
    // We need to figure out marginal rate on this contribution
  }

  const totalNewEmployeeContrib = pc.currentEmployeeContrib + taxCalcContrib;
  const withSlider = calcFullTax(pc.totalGrossIncome, isSalSac ? totalNewEmployeeContrib : pc.currentEmployeeContrib, isScottish, pc.giftAidGrossedUp);
  
  // For non-salary-sacrifice, we need to manually calculate the tax benefit
  // because calcFullTax models salary sacrifice (reducing gross pay)
  let incomeTaxSaved, niSaved;
  if (isSalSac) {
    incomeTaxSaved = baseline.incomeTax - withSlider.incomeTax;
    niSaved = baseline.ni - withSlider.ni;
  } else {
    // For relief at source / net pay: contribution reduces adjusted net income
    // which affects personal allowance taper and tax bands, but NOT NI
    const withContrib = calcFullTax(pc.totalGrossIncome, totalNewEmployeeContrib, isScottish, pc.giftAidGrossedUp);
    incomeTaxSaved = baseline.incomeTax - withContrib.incomeTax;
    niSaved = isNetPay ? 0 : 0; // relief at source and net pay don't save NI
    // For relief at source: the "tax saved" includes the basic rate HMRC claims + higher rate you reclaim
    // Actually the income tax saving IS the total relief (basic via provider + higher via SA)
    // So hmrcTopUp is the basic rate part, and higherRateReclaim is the extra
    if (usesReliefAtSource) {
      // The total income tax relief = incomeTaxSaved (which we calculated from the tax engine)
      // Of this, 20% basic rate is claimed by the provider (hmrcTopUp)
      // The rest is what you reclaim via SA
      higherRateReclaim = Math.max(0, incomeTaxSaved - hmrcTopUp);
    }
  }

  const totalTaxSaved = incomeTaxSaved + niSaved;
  const personalAllowanceRestored = (() => {
    const withContrib = calcFullTax(pc.totalGrossIncome, totalNewEmployeeContrib, isScottish, pc.giftAidGrossedUp);
    return withContrib.personalAllowance - baseline.personalAllowance;
  })();

  // Net cost to you (actual take-home reduction)
  const baselineNet = pc.totalGrossIncome - pc.currentEmployeeContrib - baseline.totalTaxNI;
  let newNet;
  if (isSalSac) {
    newNet = pc.totalGrossIncome - totalNewEmployeeContrib - withSlider.totalTaxNI;
  } else {
    // You still get your full gross pay, but you've paid outOfPocket to pension
    // and you get tax relief back (hmrc top-up + higher rate reclaim via SA)
    const taxAfterRelief = baseline.totalTaxNI - incomeTaxSaved;
    newNet = pc.totalGrossIncome - pc.currentEmployeeContrib - taxAfterRelief - outOfPocket;
  }
  const netCostToYou = baselineNet - newNet;
  const effectiveRate = additionalContrib > 0 ? ((totalIntoPot - netCostToYou) / totalIntoPot * 100) : 0;

  // Carry-forward breakdown
  const thisYearHeadroom = Math.max(0, 60000 - pc.currentTotalContrib);
  const usingThisYear = Math.min(additionalContrib, thisYearHeadroom);
  const usingCarryForward = Math.max(0, additionalContrib - thisYearHeadroom);

  // £100k trap detection (Gift Aid also reduces adjusted net income)
  const adjustedNetBefore = pc.totalGrossIncome - pc.currentEmployeeContrib - pc.giftAidGrossedUp;
  const adjustedNetAfter = pc.totalGrossIncome - totalNewEmployeeContrib - pc.giftAidGrossedUp;
  const in100kTrap = adjustedNetBefore > 100000;
  const escaped100kTrap = adjustedNetBefore > 100000 && adjustedNetAfter <= 100000;

  const formatK = (v) => v >= 1000 ? `£${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `£${Math.round(v).toLocaleString()}`;

  // Quick-set buttons
  const presets = [
    { label: "£0", val: 0 },
    thisYearHeadroom > 0 && thisYearHeadroom < pc.maxPossible && { label: `This year max (${formatK(thisYearHeadroom)})`, val: thisYearHeadroom },
    { label: `Below £100k`, val: Math.min(pc.maxPossible, Math.max(0, adjustedNetBefore - 100000)), show: in100kTrap },
    { label: `Max (${formatK(pc.maxPossible)})`, val: pc.maxPossible },
  ].filter(Boolean).filter(p => p.show === undefined || p.show);

  // Mechanism label
  const mechanisms = [
    { id: "sal_sac", label: "Salary Sacrifice", short: "Pre-tax & pre-NI", color: palette.accent },
    { id: "relief_at_source", label: "Personal Pension / SIPP", short: "You pay 80%, HMRC tops up 20%", color: palette.medium },
    { id: "net_pay", label: "Net Pay", short: "Pre-tax, NI still applies", color: palette.high },
  ];
  const activeMech = mechanisms.find(m => m.id === mechanism);

  return (
    <div style={{ background: `linear-gradient(135deg, ${palette.surface} 0%, rgba(52,211,153,0.03) 100%)`, border: `1px solid ${palette.border}`, borderRadius: 16, padding: "28px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(52,211,153,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, position: "relative" }}>
        <span style={{ fontSize: 22 }}>🎛️</span>
        <div style={{ flex: 1 }}>
          <h3 style={{ color: palette.text, fontSize: 17, fontWeight: 700, margin: 0 }}>Pension Contribution Slider</h3>
          <p style={{ color: palette.textMuted, fontSize: 12, margin: "2px 0 0" }}>
            Choose how you'll contribute, then drag to model the tax impact
          </p>
        </div>
      </div>

      {/* ===== MECHANISM TOGGLE ===== */}
      <div style={{ background: palette.bg, borderRadius: 10, padding: "4px", display: "flex", gap: 4, marginBottom: 14, position: "relative" }}>
        {mechanisms.map((m) => {
          const active = mechanism === m.id;
          return (
            <button key={m.id} onClick={() => { setMechanism(m.id); setAdditionalContrib(0); }} style={{
              flex: 1, padding: "10px 8px", borderRadius: 8, border: "none", cursor: "pointer",
              background: active ? palette.surface : "transparent",
              boxShadow: active ? `0 1px 4px rgba(0,0,0,0.2), inset 0 0 0 1px ${m.color}30` : "none",
              transition: "all 0.2s",
            }}>
              <div style={{ color: active ? m.color : palette.textDim, fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>{m.label}</div>
              <div style={{ color: active ? palette.textMuted : palette.textDim, fontSize: 10, marginTop: 2, lineHeight: 1.3 }}>{m.short}</div>
            </button>
          );
        })}
      </div>

      {/* Context note for mechanism */}
      <div style={{ background: `${activeMech.color}10`, border: `1px solid ${activeMech.color}25`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, position: "relative" }}>
        <p style={{ color: palette.textMuted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>
          {isSalSac && <><strong style={{ color: palette.accent }}>Note:</strong> Salary sacrifice must be arranged through your employer's payroll in advance — it cannot be applied retrospectively. If you're mid-year and want an immediate lump-sum contribution, switch to "Personal Pension / SIPP" above.</>}
          {usesReliefAtSource && <><strong style={{ color: palette.medium }}>Mid-year top-up:</strong> You can make a lump-sum contribution to a personal pension or SIPP before 5 April. You pay 80% of the gross amount — your provider automatically claims the 20% basic rate top-up from HMRC. If you're a higher/additional rate taxpayer, reclaim the extra via Self Assessment.</>}
          {isNetPay && <><strong style={{ color: palette.high }}>Net pay:</strong> Contributions are deducted before income tax but NI still applies on your full salary. This is arranged through your employer's payroll.</>}
        </p>
      </div>

      {/* Allowance info */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20, position: "relative" }}>
        {[
          { label: "Current employee contrib", val: formatK(pc.currentEmployeeContrib) },
          { label: "Current employer contrib", val: formatK(pc.currentEmployerContrib) },
          { label: "This year headroom", val: formatK(thisYearHeadroom) },
          { label: "3yr carry-forward available", val: formatK(pc.carryForward3yr) },
          { label: "Max additional (gross)", val: formatK(pc.maxPossible), accent: true },
        ].map((item, i) => (
          <div key={i} style={{ background: item.accent ? palette.accentBg : palette.bg, border: `1px solid ${item.accent ? "rgba(52,211,153,0.2)" : palette.border}`, borderRadius: 8, padding: "8px 12px", minWidth: 130 }}>
            <div style={{ color: palette.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{item.label}</div>
            <div style={{ color: item.accent ? palette.accent : palette.text, fontSize: 15, fontWeight: 700 }}>{item.val}</div>
          </div>
        ))}
      </div>

      {/* Quick preset buttons */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, position: "relative" }}>
        {presets.map((p, i) => (
          <button key={i} onClick={() => setAdditionalContrib(Math.round(p.val))} style={{
            background: Math.abs(additionalContrib - p.val) < 100 ? palette.accentBg : palette.bg,
            border: `1px solid ${Math.abs(additionalContrib - p.val) < 100 ? palette.accent : palette.border}`,
            borderRadius: 8, padding: "7px 14px", color: Math.abs(additionalContrib - p.val) < 100 ? palette.accent : palette.textMuted,
            fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s"
          }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* THE SLIDER */}
      <div style={{ position: "relative", padding: "8px 0 30px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ color: palette.textDim, fontSize: 11 }}>£0</span>
          <span style={{ color: activeMech.color, fontSize: 15, fontWeight: 700 }}>
            Gross contribution: £{Math.round(additionalContrib).toLocaleString()}
            {usesReliefAtSource && additionalContrib > 0 && <span style={{ color: palette.textDim, fontSize: 11, fontWeight: 400 }}> (you pay £{Math.round(outOfPocket).toLocaleString()})</span>}
          </span>
          <span style={{ color: palette.textDim, fontSize: 11 }}>{formatK(pc.maxPossible)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={pc.maxPossible}
          step={100}
          value={additionalContrib}
          onChange={(e) => setAdditionalContrib(Number(e.target.value))}
          style={{
            width: "100%", height: 8, borderRadius: 4, outline: "none", cursor: "pointer",
            appearance: "none", WebkitAppearance: "none",
            background: `linear-gradient(to right, ${activeMech.color} 0%, ${activeMech.color} ${(additionalContrib / Math.max(pc.maxPossible, 1)) * 100}%, ${palette.border} ${(additionalContrib / Math.max(pc.maxPossible, 1)) * 100}%, ${palette.border} 100%)`,
          }}
        />
        {thisYearHeadroom > 0 && thisYearHeadroom < pc.maxPossible && (
          <div style={{ position: "absolute", left: `${(thisYearHeadroom / pc.maxPossible) * 100}%`, bottom: 4, transform: "translateX(-50%)" }}>
            <div style={{ width: 1, height: 14, background: palette.high, margin: "0 auto 2px" }} />
            <span style={{ color: palette.high, fontSize: 9, whiteSpace: "nowrap" }}>carry-forward →</span>
          </div>
        )}
      </div>

      {/* Adjusted Net Income display */}
      {additionalContrib > 0 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ color: palette.textDim, fontSize: 12 }}>Adjusted Net Income:</span>
          <span style={{ color: adjustedNetAfter <= 100000 ? palette.accent : adjustedNetAfter <= 125140 ? palette.critical : palette.text, fontSize: 16, fontWeight: 700 }}>
            £{Math.round(adjustedNetAfter).toLocaleString()}
          </span>
          {adjustedNetBefore > 100000 && (
            <span style={{ color: palette.textDim, fontSize: 11 }}>
              (was £{Math.round(adjustedNetBefore).toLocaleString()})
            </span>
          )}
          {pc.giftAidGrossedUp > 0 && (
            <span style={{ color: palette.textDim, fontSize: 10, fontStyle: "italic" }}>
              incl. Gift Aid relief
            </span>
          )}
        </div>
      )}

      {/* Relief Breakdown — how the numbers relate (relief at source only) */}
      {usesReliefAtSource && additionalContrib > 0 && (
        <div style={{ background: palette.bg, borderRadius: 10, padding: "16px 18px", marginBottom: 14, position: "relative" }}>
          <div style={{ color: palette.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>How the relief works on £{Math.round(additionalContrib).toLocaleString()} gross</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: palette.textMuted, fontSize: 12 }}>You pay out of pocket</span>
              <span style={{ color: palette.high, fontSize: 14, fontWeight: 700 }}>£{Math.round(outOfPocket).toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: palette.textMuted, fontSize: 12 }}>+ HMRC basic rate top-up (20%) → into pension pot</span>
              <span style={{ color: palette.medium, fontSize: 14, fontWeight: 700 }}>£{Math.round(hmrcTopUp).toLocaleString()}</span>
            </div>
            <div style={{ height: 1, background: palette.border, margin: "4px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: palette.text, fontSize: 12, fontWeight: 600 }}>= Total into pension pot</span>
              <span style={{ color: palette.text, fontSize: 15, fontWeight: 700 }}>£{Math.round(totalIntoPot).toLocaleString()}</span>
            </div>
            {higherRateReclaim > 0 && (<>
              <div style={{ height: 1, background: palette.border, margin: "4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: palette.textMuted, fontSize: 12 }}>+ Higher/additional rate reclaim via Self Assessment → back to you</span>
                <span style={{ color: palette.high, fontSize: 14, fontWeight: 700 }}>£{Math.round(higherRateReclaim).toLocaleString()}</span>
              </div>
              <div style={{ height: 1, background: palette.border, margin: "4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: palette.textMuted, fontSize: 12, fontWeight: 600 }}>Effective net cost to you <span style={{ fontWeight: 400 }}>(out of pocket − SA reclaim)</span></span>
                <span style={{ color: palette.accent, fontSize: 15, fontWeight: 700 }}>£{Math.round(outOfPocket - higherRateReclaim).toLocaleString()}</span>
              </div>
            </>)}
          </div>
        </div>
      )}

      {/* Results Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 12, marginBottom: 16, position: "relative" }}>
        <ResultCard palette={palette} label="Income Tax Relief (total)" value={`£${Math.round(incomeTaxSaved).toLocaleString()}`} sublabel={usesReliefAtSource ? `${hmrcTopUp > 0 ? "incl. 20% HMRC top-up" : ""}${higherRateReclaim > 0 ? " + SA reclaim" : ""}` : ""} color={incomeTaxSaved > 0 ? palette.accent : palette.textDim} />
        {isSalSac && <ResultCard palette={palette} label="Employee NI Saved" value={`£${Math.round(niSaved).toLocaleString()}`} color={niSaved > 0 ? palette.accent : palette.textDim} />}
        {isSalSac && <ResultCard palette={palette} label="Employer NI Saved" value={`£${Math.round(employerNISaved).toLocaleString()}`} sublabel="(may go to your pot)" color={palette.medium} />}
        <ResultCard palette={palette} label="Total Into Pension" value={`£${Math.round(totalIntoPot).toLocaleString()}`} color={palette.text} />
        <ResultCard palette={palette} label="New Take-Home" value={`£${Math.round(newNet).toLocaleString()}`} color={palette.text} />
        <ResultCard palette={palette} label="Personal Allowance" value={`£${Math.round((() => { const wc = calcFullTax(pc.totalGrossIncome, totalNewEmployeeContrib, isScottish, pc.giftAidGrossedUp); return wc.personalAllowance; })()).toLocaleString()}`} sublabel={personalAllowanceRestored > 0 ? `+£${Math.round(personalAllowanceRestored).toLocaleString()} restored` : ""} color={personalAllowanceRestored > 0 ? palette.accent : palette.textDim} />
      </div>

      {/* TOTAL BENEFIT SUMMARY — the headline number */}
      {additionalContrib > 0 && (
        <div style={{ background: `linear-gradient(135deg, ${palette.accentBg}, rgba(52,211,153,0.04))`, border: `1px solid rgba(52,211,153,0.2)`, borderRadius: 12, padding: "18px 20px", marginBottom: 16, position: "relative" }}>
          <div style={{ color: palette.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, fontWeight: 600 }}>Total Benefit Summary</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-end" }}>
            <div>
              <div style={{ color: palette.textDim, fontSize: 11, marginBottom: 2 }}>You contribute (gross)</div>
              <div style={{ color: palette.text, fontSize: 18, fontWeight: 700 }}>£{Math.round(additionalContrib).toLocaleString()}</div>
            </div>
            <div style={{ color: palette.textDim, fontSize: 20, fontWeight: 300, lineHeight: 1 }}>→</div>
            <div>
              <div style={{ color: palette.textDim, fontSize: 11, marginBottom: 2 }}>{usesReliefAtSource ? "You actually pay" : "Reduction in take-home"}</div>
              <div style={{ color: palette.high, fontSize: 18, fontWeight: 700 }}>£{Math.round(usesReliefAtSource ? (outOfPocket - higherRateReclaim) : netCostToYou).toLocaleString()}</div>
            </div>
            <div style={{ color: palette.textDim, fontSize: 20, fontWeight: 300, lineHeight: 1 }}>→</div>
            <div>
              <div style={{ color: palette.textDim, fontSize: 11, marginBottom: 2 }}>Total tax + NI saved</div>
              <div style={{ color: palette.accent, fontSize: 22, fontWeight: 700 }}>£{Math.round(totalTaxSaved + (usesReliefAtSource ? hmrcTopUp : 0)).toLocaleString()}</div>
            </div>
            {isSalSac && employerNISaved > 0 && (<>
              <div style={{ color: palette.textDim, fontSize: 20, fontWeight: 300, lineHeight: 1 }}>+</div>
              <div>
                <div style={{ color: palette.textDim, fontSize: 11, marginBottom: 2 }}>Employer NI saved</div>
                <div style={{ color: palette.medium, fontSize: 18, fontWeight: 700 }}>£{Math.round(employerNISaved).toLocaleString()}</div>
              </div>
            </>)}
          </div>
          <div style={{ color: palette.textDim, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
            {usesReliefAtSource
              ? `For every £1 of pension contribution, you effectively pay just £${((outOfPocket - higherRateReclaim) / Math.max(additionalContrib, 1)).toFixed(2)} — the rest comes from tax relief. ${Math.round(effectiveRate)}% of the contribution is effectively free.`
              : `For every £1 sacrificed from your salary, £${(1 - netCostToYou / Math.max(additionalContrib, 1)).toFixed(2)} comes from tax and NI savings. ${Math.round(effectiveRate)}% of the contribution is effectively free.`
            }
          </div>
        </div>
      )}

      {/* Carry-forward usage bar */}
      {additionalContrib > 0 && (
        <div style={{ background: palette.bg, borderRadius: 10, padding: "14px 16px", marginBottom: 12, position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ color: palette.textMuted, fontSize: 11, fontWeight: 600 }}>Allowance Breakdown</span>
            <span style={{ color: palette.textDim, fontSize: 11 }}>
              {usingCarryForward > 0 ? "Using carry-forward" : "Within this year's allowance"}
            </span>
          </div>
          <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: 2 }}>
            <div style={{ width: `${(usingThisYear / Math.max(additionalContrib, 1)) * 100}%`, background: palette.accent, borderRadius: 5, transition: "width 0.3s" }} />
            {usingCarryForward > 0 && (
              <div style={{ width: `${(usingCarryForward / Math.max(additionalContrib, 1)) * 100}%`, background: palette.high, borderRadius: 5, transition: "width 0.3s" }} />
            )}
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
            <span style={{ color: palette.accent, fontSize: 11 }}>● This year: £{Math.round(usingThisYear).toLocaleString()}</span>
            {usingCarryForward > 0 && <span style={{ color: palette.high, fontSize: 11 }}>● Carry-forward: £{Math.round(usingCarryForward).toLocaleString()}</span>}
          </div>
        </div>
      )}

      {/* £100k trap alert */}
      {escaped100kTrap && (
        <div style={{ background: palette.accentBg, border: `1px solid rgba(52,211,153,0.25)`, borderRadius: 10, padding: "14px 18px", position: "relative" }}>
          <p style={{ color: palette.accent, fontSize: 13, fontWeight: 600, margin: "0 0 4px" }}>✅ Personal Allowance Fully Restored!</p>
          <p style={{ color: palette.textMuted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            At this contribution level, your adjusted net income drops below £100,000, restoring your full £12,570 personal allowance. This eliminates the effective 60% marginal rate in the taper zone.
          </p>
        </div>
      )}
      {in100kTrap && !escaped100kTrap && adjustedNetAfter > 100000 && additionalContrib > 0 && (
        <div style={{ background: palette.criticalBg, border: `1px solid rgba(248,113,113,0.2)`, borderRadius: 10, padding: "14px 18px", position: "relative" }}>
          <p style={{ color: palette.critical, fontSize: 13, fontWeight: 600, margin: "0 0 4px" }}>⚠️ Still in the £100k Taper Zone</p>
          <p style={{ color: palette.textMuted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            Your adjusted net income is £{Math.round(adjustedNetAfter).toLocaleString()}. Increase contributions by another £{Math.round(adjustedNetAfter - 100000).toLocaleString()} to fully escape the 60% marginal rate trap and restore your personal allowance.
          </p>
        </div>
      )}
    </div>
  );
}

function ResultCard({ palette, label, value, sublabel, color, big }) {
  return (
    <div style={{ background: palette.bg, borderRadius: 10, padding: big ? "14px 16px" : "12px 14px", border: big ? `1px solid rgba(52,211,153,0.15)` : "none" }}>
      <div style={{ color: palette.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{label}</div>
      <div style={{ color: color || palette.text, fontSize: big ? 22 : 17, fontWeight: 700, transition: "all 0.2s" }}>{value}</div>
      {sublabel && <div style={{ color: palette.textMuted, fontSize: 10, marginTop: 1 }}>{sublabel}</div>}
    </div>
  );
}

function App() {
  const [screen, setScreen] = useState("loading"); // loading | welcome | questions | report
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [animating, setAnimating] = useState(false);
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [reportData, setReportData] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [multiSelect, setMultiSelect] = useState([]);
  const [savedData, setSavedData] = useState(null); // { answers, completedAt }
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved"
  const [showEditPanel, setShowEditPanel] = useState(false);

  // Update a single answer and recalculate report
  const updateAnswer = (id, value) => {
    const newAnswers = { ...answers, [id]: value };
    setAnswers(newAnswers);
    const report = generateReport(newAnswers);
    setReportData(report);
  };

  // ===== STORAGE: Load on mount =====
  useEffect(() => {
    (async () => {
      try {
        const result = await storage.get("tax-agent-answers");
        if (result && result.value) {
          const parsed = JSON.parse(result.value);
          if (parsed && parsed.answers && Object.keys(parsed.answers).length > 0) {
            setSavedData(parsed);
          }
        }
      } catch (e) {
        // No saved data
      }
      setScreen("welcome");
    })();
  }, []);

  // ===== STORAGE: Save answers whenever they change (debounced) =====
  const saveTimeoutRef = useRef(null);
  useEffect(() => {
    if (Object.keys(answers).length === 0) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaveStatus("saving");
        await storage.set(
          "tax-agent-answers",
          JSON.stringify({
            answers,
            savedAt: new Date().toISOString(),
            completedAt: screen === "report" ? new Date().toISOString() : null,
          })
        );
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (e) {
        setSaveStatus("");
      }
    }, 800);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [answers, screen]);

  const clearSavedData = async () => {
    try {
      await storage.delete("tax-agent-answers");
    } catch (e) {}
    setSavedData(null);
  };

  const resumeSaved = () => {
    if (!savedData) return;
    const restored = savedData.answers;
    setAnswers(restored);

    // Figure out which questions are active with restored answers
    const activeQs = QUESTIONS.filter((q) => !q.condition || q.condition(restored));
    // Find first unanswered question
    let resumeIdx = activeQs.length; // default: all answered
    for (let i = 0; i < activeQs.length; i++) {
      if (restored[activeQs[i].id] === undefined) {
        resumeIdx = i;
        break;
      }
    }

    if (resumeIdx >= activeQs.length) {
      // All answered — go straight to report
      const report = generateReport(restored);
      setReportData(report);
      setScreen("report");
    } else {
      // Resume at the right question
      setCurrentQ(resumeIdx);
      setVisibleQuestions(Array.from({ length: resumeIdx + 1 }, (_, i) => i));
      setScreen("questions");
    }
  };

  const startFresh = async () => {
    await clearSavedData();
    setAnswers({});
    setCurrentQ(0);
    setVisibleQuestions([]);
    setReportData(null);
    setScreen("questions");
    setVisibleQuestions([0]);
  };

  const getActiveQuestions = useCallback(() => {
    return QUESTIONS.filter((q) => !q.condition || q.condition(answers));
  }, [answers]);

  const activeQuestions = getActiveQuestions();

  useEffect(() => {
    if (screen === "questions" && scrollRef.current) {
      setTimeout(() => {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 100);
    }
  }, [visibleQuestions, screen]);

  useEffect(() => {
    if (screen === "questions" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [currentQ, screen]);

  const startQuestions = () => {
    setScreen("questions");
    setVisibleQuestions([0]);
  };

  const handleAnswer = (value) => {
    const q = activeQuestions[currentQ];
    if (!q) return;
    setAnimating(true);
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);
    setInputValue("");
    setMultiSelect([]);

    setTimeout(() => {
      const nextQuestions = QUESTIONS.filter(
        (qq) => !qq.condition || qq.condition(newAnswers)
      );
      const nextIdx = currentQ + 1;
      if (nextIdx >= nextQuestions.length) {
        const report = generateReport(newAnswers);
        setReportData(report);
        setScreen("report");
      } else {
        setCurrentQ(nextIdx);
        setVisibleQuestions((prev) => [...prev, nextIdx]);
      }
      setAnimating(false);
    }, 300);
  };

  const handleMultiSubmit = () => {
    if (multiSelect.length > 0) handleAnswer(multiSelect);
  };

  const toggleMulti = (opt) => {
    const q = activeQuestions[currentQ];
    if (!q) return;
    const noneOption = q.options?.find(
      (o) => o === "None" || o === "None of the above"
    );
    if (opt === noneOption) {
      setMultiSelect([opt]);
    } else {
      setMultiSelect((prev) => {
        const filtered = prev.filter((p) => p !== noneOption);
        return filtered.includes(opt)
          ? filtered.filter((p) => p !== opt)
          : [...filtered, opt];
      });
    }
  };

  const currentLayer =
    screen === "questions" && activeQuestions[currentQ]
      ? activeQuestions[currentQ].layer
      : 0;
  const progress =
    screen === "questions"
      ? ((currentQ + 1) / activeQuestions.length) * 100
      : screen === "report"
      ? 100
      : 0;

  // ===== LOADING SCREEN =====
  if (screen === "loading") {
    return (
      <div style={{ background: palette.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'IBM Plex Sans', 'SF Pro Display', -apple-system, sans-serif" }}>
        <div style={{ color: palette.textMuted, fontSize: 15 }}>Loading...</div>
      </div>
    );
  }

  // ===== WELCOME SCREEN =====
  if (screen === "welcome") {
    const hasSaved = savedData && savedData.answers && Object.keys(savedData.answers).length > 0;
    const savedName = hasSaved ? savedData.answers.name : null;
    const savedDate = hasSaved && savedData.savedAt ? new Date(savedData.savedAt) : null;
    const answeredCount = hasSaved ? Object.keys(savedData.answers).length : 0;
    const isComplete = hasSaved && savedData.completedAt;

    return (
      <div style={{ background: palette.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", fontFamily: "'IBM Plex Sans', 'SF Pro Display', -apple-system, sans-serif" }}>
        
        <div style={{ maxWidth: 580, width: "100%", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: 20, background: `linear-gradient(135deg, ${palette.accentDark}, ${palette.accent})`, marginBottom: 32, boxShadow: `0 12px 40px rgba(52, 211, 153, 0.25)` }}>
            <span style={{ fontSize: 36 }}>🧮</span>
          </div>
          <h1 style={{ color: palette.text, fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 12px", lineHeight: 1.2 }}>
            UK Tax Optimisation Agent
          </h1>
          <p style={{ color: palette.textMuted, fontSize: 17, lineHeight: 1.6, margin: "0 0 16px", maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
            Answer a few questions about your circumstances and I'll identify every legal avenue to reduce your tax bill.
          </p>
          <p style={{ color: palette.textDim, fontSize: 13, lineHeight: 1.5, margin: "0 0 32px", maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>
            Covers pensions, ISAs, salary sacrifice, EIS/SEIS/VCT, marriage allowance, child benefit planning, share schemes, property reliefs, Gift Aid, and more.
          </p>

          {/* Resume card — shown when saved data exists */}
          {hasSaved && (
            <div style={{ background: palette.surface, border: `1px solid rgba(52,211,153,0.2)`, borderRadius: 14, padding: "20px 24px", marginBottom: 20, textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: palette.accentBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>💾</div>
                <div>
                  <div style={{ color: palette.text, fontSize: 14, fontWeight: 600 }}>
                    {savedName ? `Welcome back, ${savedName}` : "Saved progress found"}
                  </div>
                  <div style={{ color: palette.textDim, fontSize: 12 }}>
                    {isComplete ? "Completed assessment" : `${answeredCount} questions answered`}
                    {savedDate && ` · saved ${savedDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={resumeSaved} style={{ flex: 1, background: `linear-gradient(135deg, ${palette.accentDark}, ${palette.accent})`, color: "#fff", border: "none", borderRadius: 10, padding: "12px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
                  {isComplete ? "View My Report →" : "Continue Where I Left Off →"}
                </button>
                <button onClick={startFresh} style={{ background: palette.bg, color: palette.textMuted, border: `1px solid ${palette.border}`, borderRadius: 10, padding: "12px 18px", fontSize: 13, cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap" }} onMouseOver={(e) => { e.target.style.borderColor = palette.critical; e.target.style.color = palette.critical; }} onMouseOut={(e) => { e.target.style.borderColor = palette.border; e.target.style.color = palette.textMuted; }}>
                  Start Fresh
                </button>
              </div>
            </div>
          )}

          {/* Main CTA — only shown when no saved data */}
          {!hasSaved && (
            <button onClick={startQuestions} style={{ background: `linear-gradient(135deg, ${palette.accentDark}, ${palette.accent})`, color: "#fff", border: "none", borderRadius: 12, padding: "16px 48px", fontSize: 16, fontWeight: 600, cursor: "pointer", letterSpacing: "0.01em", boxShadow: `0 4px 20px rgba(52, 211, 153, 0.3)`, transition: "all 0.2s" }} onMouseOver={(e) => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = `0 8px 30px rgba(52, 211, 153, 0.4)`; }} onMouseOut={(e) => { e.target.style.transform = "none"; e.target.style.boxShadow = `0 4px 20px rgba(52, 211, 153, 0.3)`; }}>
              Get Started →
            </button>
          )}

          <div style={{ marginTop: 40, padding: "16px 24px", borderRadius: 10, border: `1px solid ${palette.border}`, background: palette.surface, textAlign: "left" }}>
            <p style={{ color: palette.textDim, fontSize: 11, lineHeight: 1.5, margin: 0 }}>
              <strong style={{ color: palette.textMuted }}>Disclaimer:</strong> This tool provides general tax information and education only. It does not constitute regulated financial or tax advice. Tax rules are complex and your circumstances may differ. Always consult a qualified accountant or tax adviser before making financial decisions. Figures shown are estimates based on standard HMRC rules for the selected tax year.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ===== INTERACTIVE PENSION SLIDER CALCULATOR =====
  const pensionCalc = (() => {
    if (screen !== "report") return null;
    const salary = parseFloat(answers.gross_salary) || 0;
    const bonus = parseFloat(answers.bonus_income) || 0;
    const totalEmployment = salary + bonus;
    const rentalIncome = parseFloat(answers.rental_income) || 0;
    const dividendIncome = parseFloat(answers.dividend_income) || 0;
    const freelanceIncome = parseFloat(answers.freelance_income) || 0;
    const totalGrossIncome = totalEmployment + rentalIncome + dividendIncome + freelanceIncome;
    const giftAidGrossedUp = (parseFloat(answers.gift_aid_amount) || 0) * 1.25; // Gift Aid grossed up reduces ANI
    const pensionPct = parseFloat(answers.pension_contribution_pct) || 0;
    const employerPensionPct = parseFloat(answers.employer_pension_pct) || 0;
    const currentEmployeeContrib = salary * (pensionPct / 100);
    const currentEmployerContrib = salary * (employerPensionPct / 100);
    const currentTotalContrib = currentEmployeeContrib + currentEmployerContrib;
    const isSalarySacrifice = answers.pension_type === "Salary sacrifice";

    // Carry-forward: assume last 3 years had only salary sacrifice at current rate
    // So each year used: currentTotalContrib. Unused = 60000 - currentTotalContrib per year
    const unusedPerYear = Math.max(0, 60000 - currentTotalContrib);
    const carryForward3yr = unusedPerYear * 3;
    const thisYearAllowance = 60000;
    const maxTotalAllowance = thisYearAllowance + carryForward3yr;
    // Max additional employee contribution on top of current
    const maxAdditionalContrib = Math.max(0, maxTotalAllowance - currentTotalContrib);
    // Can't sacrifice more than salary
    const maxPossible = Math.min(maxAdditionalContrib, salary - currentEmployeeContrib - 1000); // keep £1k min salary

    return { salary, bonus, totalEmployment, rentalIncome, dividendIncome, freelanceIncome, totalGrossIncome, giftAidGrossedUp, currentEmployeeContrib, currentEmployerContrib, currentTotalContrib, isSalarySacrifice, unusedPerYear, carryForward3yr, thisYearAllowance, maxTotalAllowance, maxAdditionalContrib, maxPossible: Math.max(0, maxPossible) };
  })();

  // Tax calculation engine
  const calcTaxAtIncome = (taxableIncome, isScottish) => {
    if (taxableIncome <= 0) return 0;
    if (isScottish) {
      // Scottish 2025/26 bands
      const bands = [
        { limit: 2306, rate: 0.19 },
        { limit: 13991, rate: 0.20 },
        { limit: 31092, rate: 0.21 },
        { limit: 62430, rate: 0.42 },
        { limit: 125140, rate: 0.45 },
        { limit: Infinity, rate: 0.48 },
      ];
      let tax = 0;
      let prev = 0;
      for (const b of bands) {
        if (taxableIncome <= prev) break;
        const chunk = Math.min(taxableIncome, b.limit) - prev;
        if (chunk > 0) tax += chunk * b.rate;
        prev = b.limit;
      }
      return tax;
    } else {
      // rUK 2025/26 bands
      const bands = [
        { limit: 37700, rate: 0.20 },
        { limit: 125140, rate: 0.40 },
        { limit: Infinity, rate: 0.45 },
      ];
      let tax = 0;
      let prev = 0;
      for (const b of bands) {
        if (taxableIncome <= prev) break;
        const chunk = Math.min(taxableIncome, b.limit) - prev;
        if (chunk > 0) tax += chunk * b.rate;
        prev = b.limit;
      }
      return tax;
    }
  };

  const calcFullTax = (grossIncome, pensionSalarySacrifice, isScottish, giftAidGrossedUp = 0) => {
    // Adjusted net income for personal allowance taper
    const adjustedNet = grossIncome - pensionSalarySacrifice - giftAidGrossedUp;
    let personalAllowance = 12570;
    if (adjustedNet > 100000) {
      personalAllowance = Math.max(0, 12570 - (adjustedNet - 100000) / 2);
    }
    const taxableIncome = Math.max(0, adjustedNet - personalAllowance);
    const incomeTax = calcTaxAtIncome(taxableIncome, isScottish);

    // NI: salary sacrifice reduces NI-able pay
    const niAblePay = Math.max(0, grossIncome - pensionSalarySacrifice);
    let ni = 0;
    if (niAblePay > 12570) {
      const band1 = Math.min(niAblePay, 50270) - 12570;
      const band2 = Math.max(0, niAblePay - 50270);
      ni = band1 * 0.08 + band2 * 0.02;
    }

    // Employer NI saving (informational)
    const employerNISaved = pensionSalarySacrifice * 0.138;

    return { incomeTax, ni, personalAllowance, adjustedNet, taxableIncome, employerNISaved, totalTaxNI: incomeTax + ni };
  };

  // ===== REPORT SCREEN =====
  if (screen === "report" && reportData && pensionCalc) {
    const isScottish = answers.region === "Scotland";
    const pc = pensionCalc;

    // Compute baseline (current contributions only)
    const baseline = calcFullTax(pc.totalGrossIncome, pc.currentEmployeeContrib, isScottish, pc.giftAidGrossedUp);

    return (
      <div style={{ background: palette.bg, minHeight: "100vh", fontFamily: "'IBM Plex Sans', 'SF Pro Display', -apple-system, sans-serif", padding: "0 0 60px" }}>
        {/* Header */}
        <div style={{ background: palette.surface, borderBottom: `1px solid ${palette.border}`, padding: "20px 32px", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h2 style={{ color: palette.text, fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>
                Interactive Tax Optimiser
              </h2>
              <p style={{ color: palette.textMuted, fontSize: 13, margin: "4px 0 0" }}>
                {answers.name ? `${answers.name}` : "Report"} · {answers.tax_year} · {answers.region}{isScottish ? " (Scottish rates)" : ""}
              </p>
            </div>
            {saveStatus && (
              <div style={{ color: saveStatus === "saved" ? palette.accent : palette.textDim, fontSize: 11, display: "flex", alignItems: "center", gap: 5, transition: "opacity 0.3s", opacity: saveStatus ? 1 : 0 }}>
                <span style={{ fontSize: 13 }}>{saveStatus === "saving" ? "⏳" : "✓"}</span>
                {saveStatus === "saving" ? "Saving..." : "Saved"}
              </div>
            )}
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: "28px auto 0", padding: "0 20px" }}>

          {/* Income Summary Bar */}
          <div style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", justifyContent: "space-between" }}>
              {[
                { label: "Gross Income", val: pc.totalGrossIncome },
                pc.rentalIncome > 0 && { label: "Rental", val: pc.rentalIncome },
                pc.dividendIncome > 0 && { label: "Dividends", val: pc.dividendIncome },
                { label: "Current Tax + NI", val: baseline.totalTaxNI, color: palette.critical },
                { label: "Current Net Take-Home", val: pc.totalGrossIncome - pc.currentEmployeeContrib - baseline.totalTaxNI, color: palette.accent },
              ].filter(Boolean).map((s, i) => (
                <div key={i}>
                  <div style={{ color: palette.textDim, fontSize: 11, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.label}</div>
                  <div style={{ color: s.color || palette.text, fontSize: 19, fontWeight: 700 }}>£{Math.round(s.val).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Edit Your Details toggle */}
          <button onClick={() => setShowEditPanel(!showEditPanel)} style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%",
            background: showEditPanel ? palette.surface : "transparent",
            border: `1px solid ${showEditPanel ? palette.accent + "40" : palette.border}`,
            borderRadius: 10, padding: "12px 18px", marginBottom: showEditPanel ? 0 : 20,
            borderBottomLeftRadius: showEditPanel ? 0 : 10, borderBottomRightRadius: showEditPanel ? 0 : 10,
            cursor: "pointer", transition: "all 0.2s",
            color: showEditPanel ? palette.accent : palette.textMuted, fontSize: 13, fontWeight: 600,
          }}>
            <span style={{ fontSize: 15 }}>✏️</span>
            <span>Edit Your Details</span>
            <span style={{ marginLeft: "auto", fontSize: 11, transition: "transform 0.2s", transform: showEditPanel ? "rotate(180deg)" : "none" }}>▼</span>
          </button>

          {/* Collapsible Edit Panel */}
          {showEditPanel && (
            <div style={{
              background: palette.surface, border: `1px solid ${palette.accent}40`, borderTop: "none",
              borderBottomLeftRadius: 10, borderBottomRightRadius: 10,
              padding: "20px", marginBottom: 20, maxHeight: 500, overflowY: "auto",
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
                {QUESTIONS.filter(q => {
                  if (q.condition && !q.condition(answers)) return false;
                  return answers[q.id] !== undefined;
                }).map((q) => {
                  const val = answers[q.id];
                  const isMulti = q.type === "multiselect";
                  const isSelect = q.type === "select";
                  const isCurrency = q.type === "currency" || q.type === "currency_optional";
                  const isNum = q.type === "number";

                  return (
                    <div key={q.id} style={{ background: palette.bg, borderRadius: 8, padding: "10px 12px" }}>
                      <label style={{ color: palette.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 6 }}>
                        {q.question.length > 50 ? q.question.slice(0, 50) + "…" : q.question}
                      </label>
                      {isSelect && (
                        <select
                          value={val}
                          onChange={(e) => updateAnswer(q.id, e.target.value)}
                          style={{ width: "100%", background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 6, padding: "7px 10px", color: palette.text, fontSize: 13, outline: "none", cursor: "pointer" }}
                        >
                          {q.options.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {isMulti && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {q.options.map(o => {
                            const sel = Array.isArray(val) && val.includes(o);
                            return (
                              <button key={o} onClick={() => {
                                const noneOpt = q.options.find(x => x === "None" || x === "None of the above");
                                let newVal;
                                if (o === noneOpt) { newVal = [o]; }
                                else {
                                  const filtered = (val || []).filter(v => v !== noneOpt);
                                  newVal = sel ? filtered.filter(v => v !== o) : [...filtered, o];
                                }
                                if (newVal.length === 0) newVal = [noneOpt || q.options[q.options.length - 1]];
                                updateAnswer(q.id, newVal);
                              }} style={{
                                background: sel ? `${palette.accent}15` : "transparent",
                                border: `1px solid ${sel ? palette.accent : palette.border}`,
                                borderRadius: 5, padding: "4px 8px", fontSize: 10, cursor: "pointer",
                                color: sel ? palette.accent : palette.textMuted, fontWeight: sel ? 600 : 400,
                              }}>
                                {sel ? "✓ " : ""}{o}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {(isCurrency || isNum || q.type === "text") && (
                        <div style={{ position: "relative" }}>
                          {isCurrency && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: palette.textDim, fontSize: 13 }}>£</span>}
                          <input
                            type={q.type === "text" ? "text" : "number"}
                            value={val}
                            onChange={(e) => updateAnswer(q.id, e.target.value)}
                            style={{ width: "100%", background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 6, padding: "7px 10px", paddingLeft: isCurrency ? 24 : 10, color: palette.text, fontSize: 13, outline: "none", boxSizing: "border-box" }}
                          />
                          {q.suffix && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: palette.textDim, fontSize: 12 }}>{q.suffix}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p style={{ color: palette.textDim, fontSize: 10, marginTop: 12, marginBottom: 0 }}>Changes are applied instantly and saved automatically.</p>
            </div>
          )}

          {/* ===== PENSION SLIDER ===== */}
          <PensionSlider
            pensionCalc={pc}
            baseline={baseline}
            answers={answers}
            isScottish={isScottish}
            calcFullTax={calcFullTax}
            palette={palette}
          />

          {/* Other Recommendations */}
          <h3 style={{ color: palette.text, fontSize: 18, fontWeight: 700, margin: "36px 0 16px", letterSpacing: "-0.01em" }}>
            Other Recommendations ({reportData.recommendations.filter(r => !r.title.includes("Pension") && !r.title.includes("£100K")).length})
          </h3>
          {reportData.recommendations.filter(r => !r.title.includes("Pension") && !r.title.includes("£100K")).map((rec, i) => {
            const prc = priorityColors[rec.priority];
            return (
              <div key={i} style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderLeft: `3px solid ${prc.color}`, borderRadius: 12, padding: "18px 22px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                    <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 6, background: prc.bg, color: prc.color, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{prc.label}</span>
                    <span style={{ color: palette.text, fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{rec.title}</span>
                  </div>
                  {rec.saving > 0 && (
                    <div style={{ color: palette.accent, fontSize: 17, fontWeight: 700, whiteSpace: "nowrap" }}>
                      ~£{rec.saving.toLocaleString()}<span style={{ color: palette.textDim, fontSize: 11, fontWeight: 400 }}>/yr</span>
                    </div>
                  )}
                </div>
                <p style={{ color: palette.textMuted, fontSize: 13, lineHeight: 1.65, margin: 0 }}>{rec.detail}</p>
              </div>
            );
          })}

          {/* Disclaimer */}
          <div style={{ marginTop: 36, padding: "16px 24px", borderRadius: 10, border: `1px solid ${palette.border}`, background: palette.surface }}>
            <p style={{ color: palette.textDim, fontSize: 11, lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: palette.textMuted }}>Important:</strong> Estimates based on standard HMRC rules for {answers.tax_year}. Salary sacrifice assumption: your last 3 years' contributions matched your current rate for carry-forward calculation. Actual savings depend on your specific tax code, timing, and employer arrangements. This is not regulated financial advice — consult a qualified tax adviser before acting.
            </p>
          </div>

          <button onClick={async () => { await clearSavedData(); setScreen("welcome"); setAnswers({}); setCurrentQ(0); setVisibleQuestions([]); setReportData(null); }} style={{ display: "block", margin: "28px auto 0", background: "transparent", color: palette.textMuted, border: `1px solid ${palette.border}`, borderRadius: 10, padding: "12px 32px", fontSize: 14, cursor: "pointer", transition: "all 0.2s" }} onMouseOver={(e) => { e.target.style.borderColor = palette.accent; e.target.style.color = palette.accent; }} onMouseOut={(e) => { e.target.style.borderColor = palette.border; e.target.style.color = palette.textMuted; }}>
            Start Over
          </button>
        </div>
      </div>
    );
  }

  // ===== QUESTION SCREEN =====
  const cq = activeQuestions[currentQ];

  return (
    <div style={{ background: palette.bg, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'IBM Plex Sans', 'SF Pro Display', -apple-system, sans-serif" }}>
      {/* Top Bar */}
      <div style={{ background: palette.surface, borderBottom: `1px solid ${palette.border}`, padding: "16px 24px", flexShrink: 0 }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>{LAYER_ICONS[currentLayer]}</span>
              <span style={{ color: palette.text, fontSize: 14, fontWeight: 600 }}>{LAYER_LABELS[currentLayer]}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ color: palette.textDim, fontSize: 12 }}>
                {currentQ + 1} of ~{activeQuestions.length}
              </span>
              {saveStatus && (
                <span style={{ color: saveStatus === "saved" ? palette.accent : palette.textDim, fontSize: 10, transition: "opacity 0.3s" }}>
                  {saveStatus === "saving" ? "⏳" : "✓ saved"}
                </span>
              )}
            </div>
          </div>
          <div style={{ height: 3, borderRadius: 2, background: palette.border, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${palette.accentDark}, ${palette.accent})`, borderRadius: 2, transition: "width 0.4s ease" }} />
          </div>
        </div>
      </div>

      {/* Scrollable Chat Area */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "24px 20px 120px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          {visibleQuestions.map((qIdx) => {
            const q = activeQuestions[qIdx];
            if (!q) return null;
            const answered = answers[q.id] !== undefined;
            return (
              <div key={q.id} style={{ marginBottom: 20, opacity: answered && qIdx < currentQ ? 0.5 : 1, transition: "opacity 0.3s" }}>
                {/* Question Bubble */}
                <div style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: "16px 16px 16px 4px", padding: "14px 18px", maxWidth: "85%", marginBottom: 8 }}>
                  <p style={{ color: palette.text, fontSize: 15, lineHeight: 1.5, margin: 0 }}>{q.question}</p>
                </div>
                {/* Answer Bubble */}
                {answered && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ background: palette.accentDark, borderRadius: "16px 16px 4px 16px", padding: "10px 16px", maxWidth: "75%" }}>
                      <p style={{ color: "#fff", fontSize: 14, margin: 0 }}>
                        {Array.isArray(answers[q.id])
                          ? answers[q.id].join(", ")
                          : q.type === "currency" || q.type === "currency_optional"
                          ? `£${parseFloat(answers[q.id]).toLocaleString()}`
                          : q.suffix
                          ? `${answers[q.id]}${q.suffix}`
                          : answers[q.id]}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Input Area */}
      {cq && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: palette.surface, borderTop: `1px solid ${palette.border}`, padding: "16px 20px 24px" }}>
          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            {/* Select */}
            {cq.type === "select" && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {cq.options.map((opt) => (
                  <button key={opt} onClick={() => handleAnswer(opt)} disabled={animating} style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 10, padding: "10px 18px", color: palette.text, fontSize: 14, cursor: "pointer", transition: "all 0.15s", opacity: animating ? 0.5 : 1 }} onMouseOver={(e) => { if (!animating) { e.target.style.borderColor = palette.accent; e.target.style.background = palette.accentBg; } }} onMouseOut={(e) => { e.target.style.borderColor = palette.border; e.target.style.background = palette.bg; }}>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {/* Multi-select */}
            {cq.type === "multiselect" && (
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {cq.options.map((opt) => {
                    const selected = multiSelect.includes(opt);
                    return (
                      <button key={opt} onClick={() => toggleMulti(opt)} style={{ background: selected ? palette.accentBg : palette.bg, border: `1px solid ${selected ? palette.accent : palette.border}`, borderRadius: 10, padding: "10px 18px", color: selected ? palette.accent : palette.text, fontSize: 14, cursor: "pointer", transition: "all 0.15s", fontWeight: selected ? 600 : 400 }}>
                        {selected ? "✓ " : ""}{opt}
                      </button>
                    );
                  })}
                </div>
                {multiSelect.length > 0 && (
                  <button onClick={handleMultiSubmit} style={{ background: `linear-gradient(135deg, ${palette.accentDark}, ${palette.accent})`, color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                    Continue →
                  </button>
                )}
              </div>
            )}

            {/* Text / Number / Currency Input */}
            {(cq.type === "text" || cq.type === "number" || cq.type === "currency" || cq.type === "currency_optional") && (
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  {(cq.type === "currency" || cq.type === "currency_optional") && (
                    <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: palette.textDim, fontSize: 16, fontWeight: 600 }}>£</span>
                  )}
                  <input ref={inputRef} type={cq.type === "text" ? "text" : "number"} value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (cq.type === "currency_optional" && !inputValue) handleAnswer("0");
                      else if (inputValue) handleAnswer(inputValue);
                    }
                  }} placeholder={cq.placeholder} style={{ width: "100%", background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 10, padding: `12px 16px ${cq.suffix ? "" : ""}`, paddingLeft: cq.type === "currency" || cq.type === "currency_optional" ? 32 : 16, color: palette.text, fontSize: 16, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }} onFocus={(e) => (e.target.style.borderColor = palette.borderFocus)} onBlur={(e) => (e.target.style.borderColor = palette.border)} />
                  {cq.suffix && (
                    <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: palette.textDim, fontSize: 14 }}>{cq.suffix}</span>
                  )}
                </div>
                <button onClick={() => {
                  if (cq.type === "currency_optional" && !inputValue) handleAnswer("0");
                  else if (inputValue) handleAnswer(inputValue);
                }} disabled={animating || (!inputValue && cq.type !== "currency_optional")} style={{ background: `linear-gradient(135deg, ${palette.accentDark}, ${palette.accent})`, color: "#fff", border: "none", borderRadius: 10, padding: "12px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: animating || (!inputValue && cq.type !== "currency_optional") ? 0.4 : 1, transition: "opacity 0.2s", whiteSpace: "nowrap" }}>
                  {cq.type === "currency_optional" && !inputValue ? "Skip" : "→"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
